import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '@/lib/supabaseConfig';
import { MAX_SESSION_SECONDS } from '@/lib/sessionDuration';

const HEARTBEAT_INTERVAL = 30000; // 30 seconds
const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes
const STALE_THRESHOLD = 10 * 60 * 1000; // 10 minutes — if last_active_at is older, session is stale
const SESSION_ID_KEY = 'active_session_id';
const SESSION_WORKSPACE_KEY = 'active_session_workspace';
const SESSION_STARTED_KEY = 'active_session_started';
const SESSION_ACTIVE_SECONDS_KEY = 'active_session_active_seconds';
const SESSION_LAST_TICK_KEY = 'active_session_last_tick';

// Max delta we trust per heartbeat. Anything larger means the tab was
// backgrounded / suspended and we shouldn't credit that gap as active time.
const MAX_TICK_DELTA_MS = HEARTBEAT_INTERVAL * 2;

export const SessionTracker = () => {
  const { user } = useAuth();
  const { workspace } = useWorkspace();
  const sessionIdRef = useRef<string | null>(sessionStorage.getItem(SESSION_ID_KEY));
  const lastActivityRef = useRef<number>(Date.now());
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const initializingRef = useRef(false);
  // Guard against rapid double-init when workspace.id flips twice in quick
  // succession (e.g. super-admin impersonation: leave A → enter B). Tracks
  // the last workspace id we initialized for + a small debounce window.
  const lastInitWorkspaceRef = useRef<string | null>(null);
  const initDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getStartedAt = () => {
    const stored = sessionStorage.getItem(SESSION_STARTED_KEY);
    return stored ? parseInt(stored, 10) : Date.now();
  };

  const getActiveSeconds = () => {
    const stored = sessionStorage.getItem(SESSION_ACTIVE_SECONDS_KEY);
    const n = stored ? parseInt(stored, 10) : 0;
    return Number.isFinite(n) && n > 0 ? n : 0;
  };

  const setActiveSeconds = (seconds: number) => {
    const capped = Math.max(0, Math.min(Math.floor(seconds), MAX_SESSION_SECONDS));
    sessionStorage.setItem(SESSION_ACTIVE_SECONDS_KEY, String(capped));
    return capped;
  };

  // Returns the newly accumulated total of active seconds for the session,
  // crediting the gap since the last tick only when the user was actually
  // active and the tab wasn't suspended.
  const accrueActiveSeconds = () => {
    const now = Date.now();
    const lastTickStr = sessionStorage.getItem(SESSION_LAST_TICK_KEY);
    const lastTick = lastTickStr ? parseInt(lastTickStr, 10) : now;
    const deltaMs = now - lastTick;
    sessionStorage.setItem(SESSION_LAST_TICK_KEY, String(now));

    const idleMs = now - lastActivityRef.current;
    const current = getActiveSeconds();
    if (deltaMs > 0 && deltaMs <= MAX_TICK_DELTA_MS && idleMs <= INACTIVITY_TIMEOUT) {
      return setActiveSeconds(current + Math.floor(deltaMs / 1000));
    }
    return current;
  };

  // Track user activity
  useEffect(() => {
    const handleActivity = () => {
      lastActivityRef.current = Date.now();
    };
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => window.addEventListener(event, handleActivity, { passive: true }));
    return () => {
      events.forEach(event => window.removeEventListener(event, handleActivity));
    };
  }, []);

  // Get auth token for REST calls
  const getAccessToken = useCallback(() => {
    const storageKey = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
    if (!storageKey) return '';
    try {
      const parsed = JSON.parse(localStorage.getItem(storageKey) || '{}');
      return parsed?.access_token || '';
    } catch {
      return '';
    }
  }, []);

  // Send a heartbeat update via REST with keepalive (safe for beforeunload)
  const sendHeartbeatBeacon = useCallback((id: string) => {
    const durationSeconds = accrueActiveSeconds();
    const now = new Date().toISOString();
    const body = JSON.stringify({
      last_active_at: now,
      duration_seconds: durationSeconds,
    });
    const url = `${SUPABASE_URL}/rest/v1/user_sessions?id=eq.${id}`;
    try {
      fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${getAccessToken()}`,
          'Prefer': 'return=minimal',
        },
        body,
        keepalive: true,
      }).catch(() => {});
    } catch {
      // ignore
    }
  }, [getAccessToken]);

  // End session via REST with keepalive (for logout / workspace switch)
  const endSessionBeacon = useCallback((id: string) => {
    const durationSeconds = accrueActiveSeconds();
    const now = new Date().toISOString();
    const body = JSON.stringify({
      ended_at: now,
      last_active_at: now,
      duration_seconds: durationSeconds,
    });
    const url = `${SUPABASE_URL}/rest/v1/user_sessions?id=eq.${id}`;
    try {
      fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${getAccessToken()}`,
          'Prefer': 'return=minimal',
        },
        body,
        keepalive: true,
      }).catch(() => {});
    } catch {
      // ignore
    }
  }, [getAccessToken]);

  // End a stale session row (we're not the live tab for it anymore). Uses the
  // server-side last_active_at as the end timestamp and never invents
  // wall-clock duration.
  const endSessionAsync = useCallback(async (id: string) => {
    try {
      const { data: row } = await (supabase as any)
        .from('user_sessions')
        .select('started_at, last_active_at, duration_seconds')
        .eq('id', id)
        .single();
      if (!row) return;
      const startMs = new Date(row.started_at).getTime();
      const lastActiveMs = new Date(row.last_active_at || row.started_at).getTime();
      const wallClock = Math.max(0, Math.floor((lastActiveMs - startMs) / 1000));
      const stored = typeof row.duration_seconds === 'number' && row.duration_seconds > 0
        ? row.duration_seconds
        : 0;
      const capped = Math.min(
        stored > 0 ? Math.min(stored, wallClock || stored) : wallClock,
        MAX_SESSION_SECONDS,
      );
      await (supabase as any)
        .from('user_sessions')
        .update({
          ended_at: row.last_active_at || new Date().toISOString(),
          duration_seconds: capped,
        })
        .eq('id', id);
    } catch {
      // ignore
    }
  }, []);

  const clearSession = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    sessionIdRef.current = null;
    sessionStorage.removeItem(SESSION_ID_KEY);
    sessionStorage.removeItem(SESSION_WORKSPACE_KEY);
    sessionStorage.removeItem(SESSION_STARTED_KEY);
    sessionStorage.removeItem(SESSION_ACTIVE_SECONDS_KEY);
    sessionStorage.removeItem(SESSION_LAST_TICK_KEY);
  }, []);

  // Start heartbeat — called only after session ID is confirmed
  const startHeartbeat = useCallback(() => {
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    // Anchor the tick clock so the first heartbeat doesn't credit a giant gap.
    sessionStorage.setItem(SESSION_LAST_TICK_KEY, String(Date.now()));

    heartbeatRef.current = setInterval(async () => {
      const id = sessionIdRef.current;
      if (!id) return;

      const durationSeconds = accrueActiveSeconds();
      // Skip remote write if we didn't credit any new time (user idle); avoids
      // pointless db churn.
      const lastWritten = parseInt(sessionStorage.getItem('active_session_last_written') || '0', 10);
      if (durationSeconds === lastWritten) return;
      sessionStorage.setItem('active_session_last_written', String(durationSeconds));

      try {
        await (supabase as any)
          .from('user_sessions')
          .update({
            last_active_at: new Date().toISOString(),
            duration_seconds: durationSeconds,
          })
          .eq('id', id);
      } catch {
        // ignore
      }
    }, HEARTBEAT_INTERVAL);
  }, []);

  // Close orphaned open sessions for this user+workspace (except current).
  // Uses each row's own last_active_at as the end time, never wall-clock-since-start.
  const closeOrphanedSessions = useCallback(async (userId: string, workspaceId: string, currentSessionId?: string) => {
    try {
      let fetchQuery = (supabase as any)
        .from('user_sessions')
        .select('id, started_at, last_active_at, duration_seconds')
        .eq('user_id', userId)
        .eq('workspace_id', workspaceId)
        .is('ended_at', null);

      if (currentSessionId) {
        fetchQuery = fetchQuery.neq('id', currentSessionId);
      }

      const { data: orphans } = await fetchQuery;

      if (orphans && orphans.length > 0) {
        for (const orphan of orphans) {
          const startMs = new Date(orphan.started_at).getTime();
          const lastActiveMs = new Date(orphan.last_active_at || orphan.started_at).getTime();
          const wallClock = Math.max(0, Math.floor((lastActiveMs - startMs) / 1000));
          const stored = typeof orphan.duration_seconds === 'number' && orphan.duration_seconds > 0
            ? orphan.duration_seconds
            : 0;
          const capped = Math.min(
            stored > 0 ? Math.min(stored, wallClock || stored) : wallClock,
            MAX_SESSION_SECONDS,
          );
          await (supabase as any)
            .from('user_sessions')
            .update({
              ended_at: orphan.last_active_at || new Date().toISOString(),
              duration_seconds: capped,
            })
            .eq('id', orphan.id);
        }
      }
    } catch {
      // ignore
    }
  }, []);

  // Initialize or resume session
  useEffect(() => {
    if (!user || !workspace) return;

    // Skip if we already initialized for this exact workspace id (prevents
    // double-init when workspace context briefly re-emits the same id, e.g.
    // during workspace memoization warm-up or fast impersonation switches).
    if (lastInitWorkspaceRef.current === workspace.id) return;

    // Debounce 500ms — if workspace.id flips again within the window
    // (e.g. impersonation: leave A → enter B fires twice in quick succession),
    // only the latest workspace id will actually run init.
    if (initDebounceRef.current) clearTimeout(initDebounceRef.current);
    initDebounceRef.current = setTimeout(() => {
      lastInitWorkspaceRef.current = workspace.id;
      runInit();
    }, 500);

    function runInit() {
      const existingId = sessionStorage.getItem(SESSION_ID_KEY);
      const existingWorkspace = sessionStorage.getItem(SESSION_WORKSPACE_KEY);

      // Workspace changed — end old session
      if (existingId && existingWorkspace && existingWorkspace !== workspace!.id) {
        endSessionBeacon(existingId);
        clearSession();
        // Fall through to create a new session
      }

      // Resume existing session for this workspace — but check if it's stale
      if (existingId && existingWorkspace === workspace!.id) {
        const resumeOrCreate = async () => {
          try {
            const { data: session } = await (supabase as any)
              .from('user_sessions')
              .select('last_active_at, started_at, duration_seconds')
              .eq('id', existingId)
              .single();

            if (session) {
              const lastActive = new Date(session.last_active_at).getTime();
              const isStale = Date.now() - lastActive > STALE_THRESHOLD;

              if (!isStale) {
                sessionIdRef.current = existingId;
                // Rehydrate the accumulator from the row so we keep counting
                // from where the previous tab left off instead of restarting.
                const remoteSeconds = typeof session.duration_seconds === 'number' && session.duration_seconds > 0
                  ? session.duration_seconds
                  : 0;
                setActiveSeconds(Math.max(getActiveSeconds(), remoteSeconds));
                await closeOrphanedSessions(user!.id, workspace!.id, existingId);
                startHeartbeat();
                return;
              }

              await endSessionAsync(existingId);
            }
          } catch {
            // ignore
          }

          clearSession();
          await createNewSession();
        };

        resumeOrCreate();
        return;
      }

      if (initializingRef.current) return;

      const createNew = async () => {
        await closeOrphanedSessions(user!.id, workspace!.id);
        await createNewSession();
      };

      createNew();
    }

    async function createNewSession() {
      if (initializingRef.current) return;
      initializingRef.current = true;

      try {
        const now = new Date().toISOString();
        const { data, error } = await (supabase as any)
          .from('user_sessions')
          .insert({
            user_id: user!.id,
            workspace_id: workspace!.id,
            user_agent: navigator.userAgent,
            ip_address: null,
            started_at: now,
            last_active_at: now,
            duration_seconds: 0,
          })
          .select('id')
          .single();

        if (error) {
          console.error('Failed to start session:', error);
          return;
        }

        if (data) {
          sessionIdRef.current = data.id;
          sessionStorage.setItem(SESSION_ID_KEY, data.id);
          sessionStorage.setItem(SESSION_WORKSPACE_KEY, workspace!.id);
          sessionStorage.setItem(SESSION_STARTED_KEY, Date.now().toString());
          sessionStorage.setItem(SESSION_ACTIVE_SECONDS_KEY, '0');
          sessionStorage.setItem(SESSION_LAST_TICK_KEY, String(Date.now()));
          startHeartbeat();
        }
      } catch (err) {
        console.error('Error starting session:', err);
      } finally {
        initializingRef.current = false;
      }
    }

    return () => {
      if (initDebounceRef.current) {
        clearTimeout(initDebounceRef.current);
        initDebounceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, workspace?.id]);

  // beforeunload — send final heartbeat only, do NOT end session or clear storage
  // sessionStorage survives page refreshes but is destroyed on tab close,
  // so the session will be resumed on refresh and orphaned on tab close (cleaned up on next visit)
  useEffect(() => {
    const handleBeforeUnload = () => {
      const id = sessionIdRef.current;
      if (id) {
        sendHeartbeatBeacon(id);
        // Do NOT end session or clear sessionStorage
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [sendHeartbeatBeacon]);

  // Auth state listener — end session on logout
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        const id = sessionIdRef.current;
        if (id) {
          endSessionBeacon(id);
          clearSession();
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [endSessionBeacon, clearSession]);

  // Cleanup heartbeat interval on true unmount (component removed from tree)
  // but do NOT end the session — it persists in sessionStorage
  useEffect(() => {
    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    };
  }, []);

  return null;
};
